import * as React from 'react'
import { ScrollView, Text, View } from 'react-native'
import styled from '@theme/styled-components'

import SectionTitle from '@components/sectionTitle/SectionTitle'
import ManifestationCard from './ManifestationCard'
import Button from '@components/button/Button'

import useNavigation from '@layout/useNavigation'
import useI18n from '@i18n/useI18n'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'

import useBreakpoint from '@utils/useBreakpoints'

import api from '@api/api'
import logger from '@utils/logger'
import { AuthScope } from '@api/enum'

type Props = {
  onManifestationSelected?: (manif: ManifestationResponse) => void
  hideSectionTitle?: boolean
  hideStats?: boolean
  showNotOpened?: boolean
}

const MAX_DISPLAYED = 3
const MAX_INCOMING_DISPLAYED = 5

const ManifestationsWrapper = ({ children, isPhone }: { isPhone: boolean; children: React.ReactNode }) =>
  isPhone ? (
    <ScrollContainer horizontal showsHorizontalScrollIndicator={false}>
      {children}
    </ScrollContainer>
  ) : (
    <RowContainer>{children}</RowContainer>
  )

const ManifestationsHomeSection = ({
  onManifestationSelected,
  hideSectionTitle = false,
  hideStats = false,
  showNotOpened = false,
}: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const [stats, setStats] = React.useState<StatMapping>({})

  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  React.useEffect(() => {
    if (site && scopes.includes(AuthScope.StatistiquesManifestationLecture)) {
      const manifs = scopes.includes(AuthScope.HomePersonaHotline)
        ? manifestations.inProgress
        : [
            ...manifestations.inProgress.slice(0, MAX_DISPLAYED),
            ...manifestations.incoming.slice(0, MAX_INCOMING_DISPLAYED),
            ...manifestations.toBeClosed.slice(0, MAX_DISPLAYED),
          ]

      Promise.all(manifs.map((m) => api.incident.stats(site.id, m.id)))
        .then((sts) => {
          const mapping = sts.reduce((acc, cur, idx) => ({ ...acc, [manifs[idx].id]: cur }), {} as StatMapping)
          setStats(mapping)
        })
        .catch(logger.error)
    }
  }, [manifestations, site])

  const seeAllManifestationsButton = {
    label: i18n.t('screens.site.allManifestations'),
    onClick: () => navigation.push('/manifestations'),
  }

  const onManifestationClicked = (manifestation: ManifestationResponse) => {
    if (onManifestationSelected) {
      onManifestationSelected(manifestation)
    } else {
      navigation.push(`/manifestation/${manifestation.id}`, { manifestationId: manifestation.id })
    }
  }

  const renderManifestation = (
    manifestation: ManifestationResponse,
    index: number,
    list: ManifestationResponse[],
    key: keyof OrderedManifestations
  ) => (
    <ManifestationContainer key={manifestation.id} isFirst={index === 0} isLast={index === list.length - 1}>
      <ManifestationCard
        manifestation={manifestation}
        onClick={() => onManifestationClicked(manifestation)}
        status={key}
        stats={stats[manifestation.id]}
      />
    </ManifestationContainer>
  )

  const renderListManifestation = (
    manifestation: ManifestationResponse,
    index: number,
    list: ManifestationResponse[]
  ) => {
    const isLast = index === list.length - 1
    return (
      <View key={manifestation.id}>
        <ManifestationContainer isLast={isLast} isList>
          <ManifestationCard
            list
            manifestation={manifestation}
            onClick={() => onManifestationClicked(manifestation)}
            stats={stats[manifestation.id]}
          />
        </ManifestationContainer>
        {isPhone && !isLast && <Separator key={`separator_${manifestation.id}`} />}
      </View>
    )
  }

  const renderManifestations = (key: keyof OrderedManifestations) => {
    const manifs = manifestations[key].slice(0, key === 'incoming' ? MAX_INCOMING_DISPLAYED : MAX_DISPLAYED)
    if (manifs.length === 0) {
      return <MissingInfo>{i18n.t('screens.site.noManifestation')}</MissingInfo>
    }

    if (key === 'incoming' || hideStats) {
      return manifs.map(renderListManifestation)
    }

    return (
      <ManifestationsWrapper isPhone={isPhone}>
        {manifs.map((manif, index, arr) => renderManifestation(manif, index, arr, key))}
      </ManifestationsWrapper>
    )
  }

  return (
    <>
      {!hideSectionTitle && (
        <SectionTitle
          title={i18n.t('screens.site.manifestations')}
          button={!isPhone ? seeAllManifestationsButton : undefined}
        />
      )}

      <Container>
        <Subtitle>{i18n.t('screens.site.inProgress', { count: manifestations.inProgress.length })}</Subtitle>
        {renderManifestations('inProgress')}

        {showNotOpened && (
          <>
            <Subtitle>{i18n.t('screens.site.toBeClosed', { count: manifestations.toBeClosed.length })}</Subtitle>
            {renderManifestations('toBeClosed')}

            <Subtitle>{i18n.t('screens.site.incoming', { count: manifestations.incoming.length })}</Subtitle>
            <ManifestationList>{renderManifestations('incoming')}</ManifestationList>
          </>
        )}
        {isPhone && !hideSectionTitle && (
          <View>
            <Button
              status="active"
              onClick={seeAllManifestationsButton.onClick}
              label={seeAllManifestationsButton.label}
              color="light"
            />
          </View>
        )}
      </Container>
    </>
  )
}

export default ManifestationsHomeSection

const Container = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 24)}px 0;
  ${(props) => props.theme.breakpoint === 'phone' && `margin-bottom: 24px;`}
`
const Subtitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: ${(props) => (props.theme.breakpoint === 'phone' ? 13 : 16)}px;
  color: ${(props) => props.theme.colors.secondaryText};
  margin: 12px 0;
`
const ScrollContainer = styled(ScrollView)`
  flex-direction: row;
  padding: 2px 0;
  margin: 0 -16px 18px;
`
const RowContainer = styled(View)`
  flex-flow: row wrap;
  margin-bottom: 18px;
`
const ManifestationContainer = styled(View)<{ isLast: boolean; isList?: boolean; isFirst?: boolean }>`
  ${(props) => props.theme.breakpoint === 'phone' && props.isFirst && 'margin-left: 16px;'}
  ${(props) => props.theme.breakpoint === 'phone' && props.isLast && !props.isList && 'margin-right: 16px;'}
  ${(props) => props.theme.breakpoint === 'phone' && !props.isLast && !props.isList && 'margin-right: 8px;'}
  ${(props) => (props.theme.breakpoint !== 'phone' ? `margin: 0px 16px 16px 0px;` : `padding: 8px 0px;`)}
`
const ManifestationList = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
    background-color: ${props.theme.colors.background};
    border-radius: 8px;
    `
      : `flex-wrap: wrap;`}
  ${(props) => props.theme.breakpoint === 'phone' && props.theme.constants.shadow}
`
const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
  margin: 0 0 0 16px;
`
const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;
          margin-bottom: 16px;
          `
      : `padding: 24px 0px;`}
`
